// Arabic words in a text element: which way each paragraph reads, and which
// font draws its letters — ONE answer for the editor (DOM) and the print
// (canvas raster in pdfGenerator), so the two cannot disagree.
//
// ─── WHY (#WA-269649, printed 2026-10-05) ────────────────────────────────────
//
// Every caption was « Spanish line\nArabic line ». The print laid each line out
// left-to-right, so the full stop that ENDS an Arabic sentence landed on its
// right — where an Arabic reader starts. And 'Londrina Solid' has no Arabic
// letters: each machine drew them in whatever system font it had (a thin
// sans on the Mac that made this PDF; the client's phone, another; a headless
// print server, possibly none at all).
//
//   · direction: each paragraph (a line the person typed) reads in the
//     direction of its first strong letter — Unicode rules P2/P3, which is
//     exactly what CSS `unicode-bidi: plaintext` does in the editor.
//   · font: a stack with no Arabic face gets one, named, before the generic
//     family — Cairo next to sans and display faces, Amiri next to serif ones.
//     Both are web fonts the app loads (index.html), so every machine draws
//     the same letters. Text with no Arabic letter is never touched.
//
// Pure. Guard: textBidi.test.ts

export const ARABIC_SANS_FALLBACK = 'Cairo';
export const ARABIC_SERIF_FALLBACK = 'Amiri';

const ARABIC = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFC]/;
/** Strong right-to-left letters: Hebrew, Arabic, Syriac, Thaana, NKo, and their presentation forms. */
const STRONG_RTL = /[\u0590-\u05FF\u0600-\u0608\u060B\u060D\u061B-\u064A\u066D-\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FF\u0700-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/;
/** Strong left-to-right letters (Latin, Greek, Cyrillic…): any letter that is not RTL. */
const STRONG_LTR = /\p{L}/u;

export function hasArabic(text: string | null | undefined): boolean {
  return ARABIC.test(String(text ?? ''));
}

/** The direction of one paragraph: its first strong letter decides; none → ltr. */
export function paragraphDirection(paragraph: string | null | undefined): 'rtl' | 'ltr' {
  for (const ch of String(paragraph ?? '')) {
    if (STRONG_RTL.test(ch)) return 'rtl';
    if (STRONG_LTR.test(ch)) return 'ltr';
  }
  return 'ltr';
}

/** Families that carry Arabic letters (the ones the app loads, and common system ones). */
const ARABIC_FACES = /(?:^|,)\s*['"]?(?:Cairo|Amiri|Noto (?:Sans|Naskh|Kufi) Arabic|Tajawal|Lalezar|Reem Kufi|Almarai|IBM Plex Sans Arabic|Readex Pro|Baloo Bhaijaan 2|Geeza Pro|Al Nile|Damascus)['"]?\s*(?=,|$)/i;
const GENERIC = /^(?:serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-serif|ui-sans-serif|ui-monospace|ui-rounded|emoji|math|fangsong)$/i;

/**
 * The CSS / canvas font-family stack for `text`. Unchanged unless the text has
 * Arabic letters and the stack names no Arabic face; then the right fallback
 * is inserted before the generic family (or appended when there is none).
 */
export function fontStackFor(family: string | null | undefined, text: string | null | undefined): string {
  const stack = String(family ?? '').trim();
  if (!stack || !hasArabic(text) || ARABIC_FACES.test(stack)) return stack;
  const parts = stack.split(',').map((p) => p.trim()).filter(Boolean);
  const last = parts[parts.length - 1]?.replace(/['"]/g, '') ?? '';
  const generic = GENERIC.test(last) ? last.toLowerCase() : null;
  const fallback = generic === 'serif' || (!generic && /serif/i.test(stack) && !/sans/i.test(stack))
    ? ARABIC_SERIF_FALLBACK : ARABIC_SANS_FALLBACK;
  if (generic) return [...parts.slice(0, -1), `'${fallback}'`, parts[parts.length - 1]].join(', ');
  return [...parts, `'${fallback}'`].join(', ');
}
